import { z } from "zod"
import {
  difficultySchema,
  personaPortraitUrlSchema,
  personaProfilSchema,
  realtimeVoiceSchema,
  type PersonaFall,
} from "./schemas"

const text = (label: string, max: number) =>
  z
    .string()
    .trim()
    .min(1, `${label} fehlt noch.`)
    .max(max, `Bitte höchstens ${max} Zeichen verwenden.`)
const money = z
  .number({ error: "Bitte einen Betrag eingeben." })
  .min(0, "Der Betrag darf nicht negativ sein.")
  .max(100000, "Bitte höchstens 100.000 € eingeben.")

export const personalPersonaSchema = z
  .object({
    name: text("Ein Name", 80),
    age: z
      .number()
      .int()
      .min(18, "Mindestens 18 Jahre.")
      .max(66, "Höchstens 66 Jahre für diesen Vorsorgefall."),
    profession: text("Ein Beruf", 120),
    lifeSituation: text("Die Lebenssituation", 500),
    motive: text("Ein persönliches Anliegen", 500),
    communicationStyle: text("Die Gesprächsweise", 500),
    difficulty: difficultySchema,
    voice: realtimeVoiceSchema,
    portraitUrl: personaPortraitUrlSchema,
    anlass: text("Ein Gesprächsanlass", 300),
    objection: text("Ein Einwand", 500),
    retirementAge: z.number().int().min(60).max(75),
    nettoEinkommen: money,
    bruttoEinkommen: money,
    grvBeitragMonatlich: money,
    grvAnwartschaft: money,
    privateAnwartschaft: money,
    wunschrente: money,
    /**
     * Das einzige verschachtelte Feld der ansonsten flachen Eingabe. Alle
     * Unterfelder sind optional: der Assistent belegt sie über ein Preset vor,
     * blockiert das Speichern aber nie. Was leer bleibt, landet nicht im
     * Prompt — dann erfindet die Persona dazu auch nichts.
     */
    profil: personaProfilSchema.default({}),
  })
  .superRefine((value, ctx) => {
    if (value.retirementAge <= value.age)
      ctx.addIssue({
        code: "custom",
        path: ["retirementAge"],
        message: "Der Rentenbeginn muss nach dem aktuellen Alter liegen.",
      })
    if (value.nettoEinkommen > value.bruttoEinkommen)
      ctx.addIssue({
        code: "custom",
        path: ["nettoEinkommen"],
        message: "Das Netto darf nicht höher als das Brutto sein.",
      })
    // Only flagged when both are answered; an unanswered profile stays valid.
    if (
      value.profil.finanzierungVorhanden === true &&
      value.profil.immobilienbesitz === false
    )
      ctx.addIssue({
        code: "custom",
        path: ["profil", "finanzierungVorhanden"],
        message: "Ohne Immobilie gibt es keine Finanzierung.",
      })
  })
export type PersonalPersonaInput = z.infer<typeof personalPersonaSchema>

/** Uses the same illustrative assumptions as the shipped training cases. */
export function personalPersonaFall(input: PersonalPersonaInput): PersonaFall {
  const years = input.retirementAge - input.age
  const gap = Math.max(
    0,
    input.wunschrente - input.grvAnwartschaft - input.privateAnwartschaft
  )
  const inflated = Math.round(gap * 1.02 ** years)
  const rate = (annual: number) =>
    Math.round(
      (inflated * 350) /
        (((1 + annual / 12) ** (years * 12) - 1) / (annual / 12))
    )
  return {
    anlass: input.anlass,
    passendeHooks: ["Einkommensgutachten", "digitale Renteninformation"],
    nettoEinkommen: input.nettoEinkommen,
    bruttoEinkommen: input.bruttoEinkommen,
    grvBeitragMonatlich: input.grvBeitragMonatlich,
    grvAnwartschaft: input.grvAnwartschaft,
    privateAnwartschaft: input.privateAnwartschaft,
    wunschrente: input.wunschrente,
    jahreBisRente: years,
    luecke: gap,
    lueckeMitInflation: inflated,
    rateKlassisch: rate(0.01),
    rateInvestiert: rate(0.06),
  }
}
